import React, { useRef, useState } from "react";
import { Upload, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { importMembersCsv } from "../../../../../Services/adminCorporates";
import amplitudeService from "../../../../../Services/amplitude";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../ui/dialog";
import { Button } from "../../../../../ui/button";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MiB per spec

/**
 * Single-purpose modal for CSV bulk member import.
 *
 * Required CSV columns: work_email, personal_email, first_name, last_name
 * Optional columns: department, role_title
 * Headers are case-insensitive and BOM-tolerant on the backend.
 */
const ImportCsvModal = ({ open, onOpenChange, corporateId, onImported }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showRejected, setShowRejected] = useState(false);

  const reset = () => {
    setFile(null);
    setError(null);
    setResult(null);
    setShowRejected(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    setError(null);
    if (!next) {
      setFile(null);
      return;
    }
    if (next.size > MAX_BYTES) {
      setError("File is larger than 5 MB. Please trim and try again.");
      setFile(null);
      return;
    }
    if (!/\.csv$/i.test(next.name)) {
      setError("Only .csv files are accepted.");
      setFile(null);
      return;
    }
    setFile(next);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await importMembersCsv(corporateId, file);
      setResult(data);
      amplitudeService.track?.("admin_member_imported", {
        corporate_id: corporateId,
        created: data?.created_count,
        skipped: data?.skipped_existing?.length || 0,
        rejected: data?.rejected_rows?.length || 0,
        total: data?.total_rows,
      });
      onImported?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "CSV import failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (next) => {
    if (busy) return;
    onOpenChange(next);
    if (!next) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import members from CSV</DialogTitle>
          <DialogDescription>
            Upload a .csv file (≤ 5 MB) to add multiple members at once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
            <p className="font-medium mb-1">Expected columns</p>
            <code className="block text-xs font-mono">
              work_email,personal_email,first_name,last_name,department,role_title
            </code>
            <p className="text-xs text-muted-foreground mt-1">
              The last two columns are optional. Headers are case-insensitive.
            </p>
          </div>

          {!result && (
            <>
              <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-background px-6 py-10 cursor-pointer hover:bg-muted/30 transition">
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {file ? file.name : "Click to choose a .csv file"}
                </span>
                {file && (
                  <span className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md border bg-emerald-50 px-3 py-3">
                  <div className="text-2xl font-semibold text-emerald-700">
                    {result.created_count ?? 0}
                  </div>
                  <div className="text-xs text-emerald-700">Created</div>
                </div>
                <div className="rounded-md border bg-amber-50 px-3 py-3">
                  <div className="text-2xl font-semibold text-amber-700">
                    {result.skipped_existing?.length ?? 0}
                  </div>
                  <div className="text-xs text-amber-700">Skipped (existing)</div>
                </div>
                <div className="rounded-md border bg-rose-50 px-3 py-3">
                  <div className="text-2xl font-semibold text-rose-700">
                    {result.rejected_rows?.length ?? 0}
                  </div>
                  <div className="text-xs text-rose-700">Rejected</div>
                </div>
              </div>

              {result.rejected_rows?.length > 0 && (
                <div className="rounded-md border">
                  <button
                    type="button"
                    onClick={() => setShowRejected((v) => !v)}
                    className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Rejected rows ({result.rejected_rows.length})
                    </span>
                    {showRejected ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {showRejected && (
                    <div className="max-h-72 overflow-auto border-t">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">Work email</th>
                            <th className="px-3 py-2 text-left">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.rejected_rows.map((row) => (
                            <tr key={row.row_number} className="border-t">
                              <td className="px-3 py-1.5 tabular-nums">
                                {row.row_number}
                              </td>
                              <td className="px-3 py-1.5 font-mono">
                                {row.work_email || "—"}
                              </td>
                              <td className="px-3 py-1.5 text-destructive">
                                {row.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleClose(false)}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!file || busy}>
                {busy ? "Importing…" : "Import"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCsvModal;
